import { useCallback, type FocusEvent } from 'react';
import { Controller, type Control, type UseFormResetField } from 'react-hook-form';

import { useTranslate, useValidate, type CardOptionType } from 'hooks';
import type { PaymentType } from 'types';
import { renderIcon } from 'utils';
import { Input } from 'views/components/fonbet/ui';

import { type CardFieldsType } from './type';

type Props = {
  control: Control<CardFieldsType, any>;
  selectedCard: CardOptionType;
  cardNumber: string;
  paymentData: PaymentType;
  amount: string;
  onHandleFocus: (e: FocusEvent<HTMLInputElement>) => void;
  resetField: UseFormResetField<CardFieldsType>;
};

const PanInput = ({
  control,
  selectedCard,
  cardNumber,
  paymentData,
  amount,
  onHandleFocus,
  resetField,
}: Props) => {
  const { t } = useTranslate();
  const { validateCardNumber } = useValidate();

  const renderCardIcon = useCallback(() => {
    if (!selectedCard.newCard && selectedCard.bankBin) {
      return renderIcon(selectedCard.bankBin);
    }

    if (cardNumber) {
      return renderIcon(cardNumber);
    }

    return undefined;
  }, [cardNumber, selectedCard]);

  return (
    <Controller
      name="pan"
      control={control}
      rules={
        selectedCard.newCard
          ? {
              required: true,
              validate: validateCardNumber,
            }
          : undefined
      }
      render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
        <Input
          placeholder={t('fonbet.card-number.placeholder')}
          LeftIcon={renderCardIcon()}
          mask=" "
          name="pan"
          label={t('fonbet.card-number.label')}
          format={selectedCard.newCard ? '#### #### #### ####' : selectedCard.maskedPan}
          inputClassName="w-full"
          allowEmptyFormatting={!selectedCard.newCard}
          value={value}
          ref={ref}
          clear={() => resetField('pan')}
          onChange={onChange}
          onFocus={onHandleFocus}
          type="tel"
          error={error?.message}
          disabled={paymentData.commission > amount || !selectedCard.newCard}
        />
      )}
    />
  );
};

export default PanInput;
